import React, { useState, useEffect } from 'react'
import {
  Box,
  TextField,
  Dialog,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Autocomplete,
  Switch,
  FormControlLabel,
  Checkbox
} from '@mui/material'
import moment from 'moment'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useGetPlanUsageMutation, useUpdatePlanUsageMutation } from 'services/auth-service'
import Divider from '@mui/material/Divider'
import { planSettingFormFields } from 'config'
import { useLazyGetUserCurrentWorkspaceQuery } from 'services/user-service'

const PlanSetting = ({ onClose, open, userId, allWorkspaces, isPartnerAccount, email }) => {
  const [updatePlan] = useUpdatePlanUsageMutation()
  const [getPlanUsage] = useGetPlanUsageMutation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedWorkspace, setSelectedWorkspace] = useState(null)
  const [workspaceData, setWorkspaceData] = useState(null)
  const [getPartnerCurrentWorkspace] = useLazyGetUserCurrentWorkspaceQuery();

  useEffect(() => {
    const fetchCurrentWorkspace = async () => {
      const {data: resCurrentWorkspace} = await getPartnerCurrentWorkspace({ partnerId: userId })
      if (resCurrentWorkspace?.currentWorkspace) {
        setSelectedWorkspace({_id : resCurrentWorkspace?.currentWorkspace});
      }
    }
    if (userId) {
      fetchCurrentWorkspace();
    }
  }, [userId])

  const sendUpdatedPlan = async (updatedData) => {
    try {
      setIsSubmitting(true)
      await updatePlan({
        id: userId,
        data: updatedData
      })
    } catch (error) {
      console.error(error)
    }
    setIsSubmitting(false)
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      activeLeads: 0,
      activeLeadsRecurring: false,
      monthlyEmails: 0,
      monthlyEmailsRecurring: false,
      aiWriterCredits: 0,
      aiWriterCreditsRecurring: false,
      leadsCredits: 0,
      leadsCreditsRecurring: false,
      bounceCredit: 0,
      bounceCreditRecurring: false,
      linkedinCredits: 0,
      linkedinCreditsRecurring: false,
      dailyLeadLimit: 0,
      dailyLeadLimitRecurring: false,
      emailAccountsLimit: 0,
      emailAccountsLimitRecurring: false,
      savedContacts: 0,
      savedContactsRecurring: false,
      dealPipeline: 0,
      dealPipelineRecurring: false,
      dataEnrichmentCredits: 0,
      dataEnrichmentCreditsRecurring: false,
      buyerIntendDataTopiscs: 0,
      buyerIntendDataTopiscsRecurring: false,
      // freeTrialExpiresAt: null,
      // leadsExpiresAt: null,
      // sendingWarmupExpiresAt: null,
      selectedWorkspace: selectedWorkspace?._id || '',
      perDay: 0,
      perHour: 0,
      perMin: 0,
      buyerIntendData: false,
      advancedFilters: false,
      abTesting: false,
      aiFilters: false,
      mailboxConnection: false,
      customDomainTracking: false,
      globalBlocklist: false,
      emailWarmup: false,
      multiChannelLinkedinOutreach: false,
      unibox: false,
      aiAutoReplies: false,
      zapierIntegration: false,
      hubspotIntegration: false,
      salesforceIntegration: false,
      pipedriveIntegration: false,
      webhookIntegration: false,
      advancedApis: false,
      enableAllIntegration: false,
      enableCreditUsage: false
    },
    validationSchema: Yup.object({
      activeLeads: Yup.number().integer(),
      activeLeadsRecurring: Yup.boolean(),
      monthlyEmails: Yup.number().integer(),
      monthlyEmailsRecurring: Yup.boolean(),
      aiWriterCredits: Yup.number().integer(),
      aiWriterCreditsRecurring: Yup.boolean(),
      leadsCredits: Yup.number().integer(),
      leadsCreditsRecurring: Yup.boolean(),
      linkedinCredits: Yup.number().integer(),
      linkedinCreditsRecurring: Yup.boolean(),
      bounceCredit: Yup.number().integer(),
      bounceCredit_recurring: Yup.boolean(),
      dailyLeadLimit: Yup.number().integer(),
      dailyLeadLimitRecurring: Yup.boolean(),
      emailAccountsLimit: Yup.number().integer(),
      emailAccountsLimitRecurring: Yup.boolean(),
      savedContacts: Yup.number().integer(),
      savedContactsRecurring: Yup.boolean(),
      dealPipeline: Yup.number().integer(),
      dealPipelineRecurring: Yup.boolean(),
      dataEnrichmentCredits: Yup.number().integer(),
      dataEnrichmentCreditsRecurring: Yup.boolean(),
      buyerIntendDataTopiscs: Yup.number().integer(),
      buyerIntendDataTopiscsRecurring: Yup.boolean(),
      // freeTrialExpiresAt: Yup.date().nullable(),
      // leadsExpiresAt: Yup.date().nullable(),
      // sendingWarmupExpiresAt: Yup.date().nullable(),
      selectedWorkspace: Yup.string().required('Workspace is required'),
      perDay: Yup.number().integer(),
      perHour: Yup.number().integer(),
      buyerIntendData: Yup.boolean(),
      advancedFilters: Yup.boolean(),
      abTesting: Yup.boolean(),
      aiFilters: Yup.boolean(),
      mailboxConnection: Yup.boolean(),
      customDomainTracking: Yup.boolean(),
      globalBlocklist: Yup.boolean(),
      emailWarmup: Yup.boolean(),
      multiChannelLinkedinOutreach: Yup.boolean(),
      unibox: Yup.boolean(),
      aiAutoReplies: Yup.boolean(),
      zapierIntegration: Yup.boolean(),
      hubspotIntegration: Yup.boolean(),
      salesforceIntegration: Yup.boolean(),
      pipedriveIntegration: Yup.boolean(),
      webhookIntegration: Yup.boolean(),
      advancedApis: Yup.boolean(),
      enableAllIntegration: Yup.boolean(),
      enableCreditUsage: Yup.boolean()
    }),
    onSubmit: async (values) => {
      await sendUpdatedPlan(values)
      await onClose()
    }
  })

  useEffect(() => {
    const fetchWorkspaceData = async () => {
      if (selectedWorkspace) {
        try {
          const { data } = await getPlanUsage(selectedWorkspace._id)
          setWorkspaceData(data)

          formik.setValues({
            ...formik.values,
            selectedWorkspace: selectedWorkspace._id,
            activeLeadsRecurring: data?.usage?.activeLeadsRecurring,
            monthlyEmailsRecurring: data?.usage?.monthlyEmailsRecurring,
            aiWriterCreditsRecurring: data?.usage?.aiWriterCreditsRecurring,
            leadsCreditsRecurring: data?.usage?.leadsCreditsRecurring,
            linkedinCreditsRecurring: data?.usage?.linkedinCreditsRecurring,
            bounceCreditRecurring: data?.usage?.bounceCreditRecurring,
            dailyLeadLimitRecurring: data?.usage?.dailyLeadLimitRecurring,
            emailAccountsLimitRecurring: data?.usage?.emailAccountsLimitRecurring,
            savedContactsRecurring: data?.usage?.savedContactsRecurring,
            dealPipelineRecurring: data?.usage?.dealPipelineRecurring,
            dataEnrichmentCreditsRecurring: data?.usage?.dataEnrichmentCreditsRecurring,
            buyerIntendDataTopiscsRecurring: data?.usage?.buyerIntendDataTopiscsRecurring,
            activeLeads: data?.usage?.activeLeads,
            monthlyEmails: data?.usage?.monthlyEmails,
            aiWriterCredits: data?.usage?.aiWriterCredits,
            leadsCredits: data?.usage?.leadsCredits,
            linkedinCredits: data?.usage?.linkedinCredits,
            bounceCredit: data?.usage?.bounceCredit,
            dailyLeadLimit: data?.usage?.dailyLeadLimit,
            emailAccountsLimit: data?.usage?.emailAccountsLimit,
            savedContacts: data?.usage?.savedContacts,
            dealPipeline: data?.usage?.dealPipeline,
            dataEnrichmentCredits: data?.usage?.dataEnrichmentCredits,
            buyerIntendDataTopiscs: data?.usage?.buyerIntendDataTopiscs,
            perDay: data?.plan?.apiRateLimit?.limits?.perDay,
            perHour: data?.plan?.apiRateLimit?.limits?.perHour,
            perMin: data?.plan?.apiRateLimit?.limits?.perMin,
            // freeTrialExpiresAt:
            //   data?.usage?.freeTrialExpiresAt ||
            //   data?.plan?.freeTrialExpiresAt,
            // leadsExpiresAt: data?.plan?.subscription?.leads?.expiresAt || null,
            // sendingWarmupExpiresAt: data?.plan?.subscription?.sendingWarmup?.expiresAt || null,
            buyerIntendData: data?.usage?.buyerIntendData,
            advancedFilters: data?.usage?.advancedFilters,
            abTesting: data?.usage?.abTesting,
            aiFilters: data?.usage?.aiFilters,
            mailboxConnection: data?.usage?.mailboxConnection,
            customDomainTracking: data?.usage?.customDomainTracking,
            globalBlocklist: data?.usage?.globalBlocklist,
            emailWarmup: data?.usage?.emailWarmup,
            multiChannelLinkedinOutreach: data?.usage?.multiChannelLinkedinOutreach,
            unibox: data?.usage?.unibox,
            aiAutoReplies: data?.usage?.aiAutoReplies,
            zapierIntegration: data?.usage?.zapierIntegration,
            hubspotIntegration: data?.usage?.hubspotIntegration,
            salesforceIntegration: data?.usage?.salesforceIntegration,
            pipedriveIntegration: data?.usage?.pipedriveIntegration,
            webhookIntegration: data?.usage?.webhookIntegration,
            advancedApis: data?.usage?.advancedApis,
            enableAllIntegration: data?.usage?.enableAllIntegration || false,
            enableCreditUsage: data?.usage?.enableCreditUsage || false
          })
        } catch (error) {
          toast.error(`${error.message || "Error fetching workspace data"}`)
        }
      }
    }

    fetchWorkspaceData()
  }, [selectedWorkspace])

  const handleClose = () => {
    onClose()
  }

  const paidUser = workspaceData?.plan?.subscription?.sendingWarmup?.expiresAt
  const weeklyTrial = workspaceData?.plan?.subscription?.sendingWarmup?.planType

  const handleKeyDown = (e) => {
    // Prevent entering invalid characters
    const regex = /^[+-.]*$/ // Only allow digits
    if (regex.test(e.key)) {
      e.preventDefault()
    }
  }

  return (
    <>
      <Dialog onClose={handleClose} open={open}>
        <form
          noValidate
          onSubmit={formik.handleSubmit}
          className={{ width: '500px' }}
        >
          <Box
            maxWidth='sm'
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              width: 'full',
              margin: '1rem',
              bgcolor: 'white',
              padding: '1rem'
            }}
          >
            <Typography
              variant='h3'
              component='h2'
              sx={{ cursor: 'pointer', color: 'rgba(40, 40, 123, 1)' }}
            >
              Plan Settings
            </Typography>
            {email &&
              <Typography
                variant='h6'
                component='h2'
                sx={{ cursor: 'pointer' }}
              >
                User Email : {email}
              </Typography>
            }
            {isPartnerAccount || paidUser === undefined || weeklyTrial === 'weekly'
              ? (<></>)
              : (
                <Typography
                  variant='h6'
                  component='h2'
                  sx={{ cursor: 'pointer', color: 'red' }}
                >
                  The expiration date cannot be modified for paid accounts.
                </Typography>
                )}
            {planSettingFormFields.map((section) => (
              <Box key={section.title} sx={{ mt: 3 }}>
                {!(section?.dateFields || section?.workspaceField) &&
                  <Divider sx={{ mt: 3, mb: 2 }} textAlign='center'>
                    <Typography variant='h6' color='textSecondary'>
                      {section.title}
                    </Typography>
                  </Divider>}
                <Grid container spacing={2}>
                  {section.textFields?.map((field) => (
                    <React.Fragment key={field.name}>
                      <Grid item xs={12} md={6} key={field.name}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 1 }}>
                          <Typography
                            sx={{
                              fontSize: '16px',
                              fontWeight: 700,
                              lineHeight: '26px',
                              color: 'rgb(129, 129, 176)'
                            }}
                          >
                            {field.label}
                          </Typography>

                          {/* RECURRING CHECKBOX */}
                          {
                            section.title === "Credits" && field.recurringName && (
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    name={field.recurringName}
                                    checked={formik.values[field.recurringName]}
                                    onChange={formik.handleChange}
                                  />
                                }
                                label="Recurring"
                              />
                            )
                          }
                        </Box>
                        <TextField
                          fullWidth
                          variant='outlined'
                          placeholder={field.label}
                          name={field.name}
                          value={formik.values[field.name]}
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          onKeyDown={handleKeyDown}
                          error={formik.touched[field.name] && Boolean(formik.errors[field.name])}
                          helperText={formik.touched[field.name] && formik.errors[field.name]}
                          sx={{
                            '& div': {
                              pl: 0.3,
                              borderRadius: '0.7rem',
                              width: '20rem',
                              mt: '-0.2rem'
                            },
                            '& div fieldset': {
                              border: '1px solid #E4E4E5'
                            },

                            '& div input': {
                              py: 1.3,
                              fontSize: '13px',
                              fontWeight: 400,
                              lineHeight: '16px',
                              letterSpacing: '0em',
                              '&::placeholder': {
                                color: 'rgba(40, 40, 123, 0.5)'
                              }
                            }
                          }}
                        />
                      </Grid>                     
                    </React.Fragment>
                  ))}
                  {(section?.dateFields || section?.workspaceField) &&
                    <Grid item xs={12} md={6}>
                      {section.dateFields?.filter((field) => formik.values[field.name] !== null)?.map((field) => (
                        <Box key={field.name}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 1 }}>
                            <Typography
                              sx={{
                                fontSize: '16px',
                                fontWeight: 700,
                                lineHeight: '26px',
                                color: 'rgb(129, 129, 176)'
                              }}
                            >
                              {field.label}
                            </Typography>
                          </Box>
                          <TextField
                            fullWidth
                            type='date'
                            variant='outlined'
                            name={field.name}
                            placeholder={field.value}
                            value={moment(formik.values[field.name]).format(
                              'YYYY-MM-DD'
                            )}
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                            onKeyDown={handleKeyDown}
                            error={formik.touched[field.name] && Boolean(formik.errors[field.name])}
                            helperText={formik.touched[field.name] && formik.errors[field.name]}
                            sx={{
                              '& div': {
                                pl: 0.3,
                                borderRadius: '0.7rem',
                                mt: '-0.2rem'
                              },
                              '& div fieldset': {
                                border: '1px solid #E4E4E5'
                              },

                              '& div input': {
                                py: 1.3,
                                fontSize: '13px',
                                fontWeight: 400,
                                lineHeight: '16px',
                                letterSpacing: '0em',
                                '&::placeholder': {
                                  color: 'rgba(40, 40, 123, 0.5)'
                                }
                              }
                            }}
                          />
                        </Box>
                      ))}
                      {section.workspaceField?.map((field) => (
                        <Autocomplete
                          key={field.name}
                          id='workspace-autocomplete'
                          options={allWorkspaces}
                          getOptionLabel={(option) => option.name}
                          value={
                            allWorkspaces.find((workspace) => workspace._id === formik.values[field.name]) || null
                          }
                          onChange={(event, newValue) => {
                            setSelectedWorkspace(newValue)
                          }}
                          disabled={isPartnerAccount}
                          onBlur={formik.handleBlur}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={field.label}
                              error={formik.touched[field.name] && Boolean(formik.errors[field.name])}
                              helperText={formik.touched[field.name] && formik.errors[field.name]}
                            />
                          )}
                        />
                      ))}
                    </Grid>}
                  {section.switches?.map((sw) => (
                    <Grid item xs={6} key={sw.name}>
                      <FormControlLabel
                        control={
                          <Switch
                            name={sw.name}
                            checked={formik.values[sw.name]}
                            onChange={formik.handleChange}
                          />
                        }
                        label={sw.label}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))}

            <Box sx={{ display: 'flex', justifyContent: 'end', mt: '2rem' }}>
              <SaveButton
                onClick={formik.handleSubmit}
                isSubmitting={isSubmitting}
              />
            </Box>
          </Box>
        </form>
      </Dialog>
    </>
  )
}

export default PlanSetting

const SaveButton = ({ onClick, isSubmitting }) => {
  return (
    <Button
      sx={{
        display: 'flex',
        justifyContent: 'end',
        alignItems: 'center',
        fontSize: '14px',
        fontWeight: 700,
        lineHeight: '18px',
        py: 1,
        px: 2
      }}
      variant='contained'
      type='submit'
      onClick={onClick}
    >
      {isSubmitting
        ? (
          <>
            <CircularProgress
              color='inherit'
              size={20}
              thickness={5}
              sx={{ mr: 1 }}
            />
            Saving
          </>
          )
        : (
          <>Save</>
          )}
    </Button>
  )
}
